import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Layout, Form, Button, Spin, Tabs, Breadcrumb, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "antd/dist/antd.min.css";
import "../../styles/Main.scss";
import { authUserData } from "../../utils/Helper";
import { getRequest, patchRequest, postRequest } from "../../axios";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import MaterialMasterCategoryWise from "./MaterialMasterCategoryWise";
import { useNavigate } from "react-router-dom";
import SubscriptionNotification from "../../authentication/SubscriptionNotification";

const { Content } = Layout;

const INITIAL_STATE = {
  category_wise: [],
  company: null,
};

const MaterialListCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef();

  const [state, setState] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("Cement");
  const [selectAllChecked, setSelectAllChecked] = useState({});
  const [activeKey, setActiveKey] = useState("1");
  const [unitOfMeasurement, setUnitOfMeasurement] = useState([]);
  const [estimatedPrices, setEstimatedPrices] = useState({});
  const [erpCodes, setERPCodes] = useState({});
  const [priceMode, setPriceMode] = useState("common");
  const [btnLoading, setBtnLoading] = useState(false);
  const CompanyId = authUserData()?.company || null;

  const MaterialListCompanyGetById = async () => {
    setLoading(true);
    try {
      const apiResponse = await getRequest(
        `company-material-code/get-material-details/`,
      );
      if (apiResponse.data.data) {
        const transformedData = {
          ...apiResponse.data.data,
          category_wise: apiResponse.data.data.category_wise.map(
            (category) => ({
              ...category,
              company_material_master: category.company_material_master.map(
                (master) => ({
                  ...master,
                  material_details: master.material_details.map((detail) => {
                    const diameterPricesObj = {};
                    if (
                      detail.diameter_prices &&
                      Array.isArray(detail.diameter_prices)
                    ) {
                      detail.diameter_prices.forEach((dp) => {
                        if (dp.diameter_value !== null) {
                          const formattedDiameterValue = Number(
                            dp.diameter_value,
                          ).toFixed(2);
                          diameterPricesObj[formattedDiameterValue] = {
                            estimated_price:
                              dp.estimated_price?.toString() || "0",
                            diameter_id: dp.diameter_id,
                          };
                        }
                      });
                    }
                    return {
                      ...detail,
                      diameter_prices: diameterPricesObj,
                      pricing_type: detail.pricing_type || "common",
                    };
                  }),
                }),
              ),
            }),
          ),
        };

        setState(transformedData);

        const uomOptions = apiResponse.data.data.reduce((acc, current) => {
          current.material_details.forEach((material) => {
            if (!acc.includes(material.unit_of_measurement_name)) {
              acc.push(material.unit_of_measurement_name);
            }
          });
          return acc;
        }, []);

        setUnitOfMeasurement(uomOptions);
        setTotalCount(apiResponse.data.count);
      } else {
        ErrorNotificationMsg(t("error_in_list_prapproval_hierarchy"));
      }
    } catch (error) {
      console.error("Error fetching material details:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeDimensionAndSpecification = useCallback((state) => {
    const newState = { ...state };
    newState.category_wise = newState.category_wise.map((category) => ({
      ...category,
      company_material_master: category.company_material_master.map(
        (master) => {
          if (master.sub_category === "RMC") {
            return {
              ...master,
              material_details: master.material_details.map(
                ({ dimension_standard, specification, ...rest }) => ({
                  ...rest,
                  unit_of_measurement: 4,
                  unit_of_measurement_name: "Cubic Meter",
                }),
              ),
            };
          }
          return {
            ...master,
            material_details: master.material_details.map(
              ({ dimension_standard, specification, ...rest }) => rest,
            ),
          };
        },
      ),
    }));
    return newState;
  }, []);

  const handleDiameterPriceChange = useCallback(
    (categoryIndex, subIndex, detailIndex, diameterValue, price) => {
      setState((prevState) => {
        const updatedCategoryWise = [...prevState.category_wise];
        const updatedMaterialDetails = [
          ...updatedCategoryWise[categoryIndex].company_material_master[
            subIndex
          ].material_details,
        ];
        const updatedDetail = { ...updatedMaterialDetails[detailIndex] };

        updatedDetail.diameter_prices = updatedDetail.diameter_prices || {};
        updatedDetail.diameter_prices[diameterValue] = price;

        updatedMaterialDetails[detailIndex] = updatedDetail;
        updatedCategoryWise[categoryIndex].company_material_master[
          subIndex
        ].material_details = updatedMaterialDetails;

        return {
          ...prevState,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [],
  );

  const handleSubmit = async () => {
    try {
      setBtnLoading(true);

      const percentage = authUserData()?.company_profile_perc || null;
      const updatedState = removeDimensionAndSpecification(state);

      const dataToSend = {
        ...updatedState,
        category_wise: updatedState.category_wise.map((category) => ({
          ...category,
          company_material_master: category.company_material_master.map(
            (master) => ({
              ...master,
              material_details: master.material_details.map((detail) => {
                const { is_new, ...detailWithoutIsNew } = detail;

                if (detail.pricing_type === "common") {
                  return {
                    ...detailWithoutIsNew,
                    diameter_prices: [],
                    estimated_price: Number(detail.estimated_price) || 0,
                  };
                } else {
                  if (
                    detail.diameter_prices &&
                    Object.keys(detail.diameter_prices).length > 0
                  ) {
                    const uniqueDiameterPrices = new Map();

                    Object.entries(detail.diameter_prices).forEach(
                      ([diameter_value, priceData]) => {
                        if (
                          diameter_value &&
                          priceData.estimated_price !== null &&
                          priceData.estimated_price !== undefined
                        ) {
                          uniqueDiameterPrices.set(Number(diameter_value), {
                            diameter_id: priceData.diameter_id,
                            estimated_price: Number(priceData.estimated_price),
                          });
                        }
                      },
                    );

                    const sortedDiameterPrices = Array.from(
                      uniqueDiameterPrices.values(),
                    ).sort((a, b) => a.diameter_id - b.diameter_id);

                    return {
                      ...detailWithoutIsNew,
                      diameter_prices: sortedDiameterPrices,
                      estimated_price: 0,
                    };
                  }
                  return {
                    ...detailWithoutIsNew,
                    diameter_prices: [],
                    estimated_price: 0,
                  };
                }
              }),
            }),
          ),
        })),
      };

      let response;

      if (percentage == 100) {
        response = await patchRequest(
          `company-material-code/${CompanyId}/`,
          dataToSend,
        );
      } else {
        response = await postRequest(`company-material-code/`, dataToSend);
      }

      if (response.data.success) {
        SuccessNotificationMsg(
          t(
            percentage < 100
              ? "Frequently purchased material selected for future PR planning"
              : "Frequently purchased material updated for future PR planning",
          ),
        );
        navigate(percentage !== 100 && "/site-location-step");
      }
    } catch (error) {
      console.error("Error saving material details:", error);
      ErrorNotificationMsg(t("error_saving_material_details"));
    } finally {
      setBtnLoading(false);
    }
  };

  const handleEstimatedPriceChange = useCallback(
    (categoryIndex, subCategoryIndex, detailIndex, value) => {
      const numericValue = value.replace(/,/g, "");
      if (isNaN(numericValue)) return;

      setEstimatedPrices((prev) => ({
        ...prev,
        [`${categoryIndex}_${subCategoryIndex}_${detailIndex}`]: numericValue,
      }));

      setState((prevState) => {
        const updatedCategoryWise = [...prevState.category_wise];
        updatedCategoryWise[categoryIndex].company_material_master[
          subCategoryIndex
        ].material_details[detailIndex].estimated_price = numericValue;
        return {
          ...prevState,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [],
  );

  const handleERPCodeChange = useCallback(
    (categoryIndex, subCategoryIndex, detailIndex, value) => {
      setERPCodes((prev) => ({
        ...prev,
        [`${categoryIndex}_${subCategoryIndex}_${detailIndex}`]: value,
      }));

      setState((prevState) => {
        const updatedCategoryWise = [...prevState.category_wise];
        updatedCategoryWise[categoryIndex].company_material_master[
          subCategoryIndex
        ].material_details[detailIndex].company_material_code = value;
        return {
          ...prevState,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [],
  );

  const handleAddressChange = useCallback(
    (categoryIndex, subCategoryIndex, detailIndex, value) => {
      const unitOfMeasurementt = unitOfMeasurement.find((i) => i.id === value);

      setState((prevState) => {
        const updatedCategoryWise = [...prevState.category_wise];
        const updatedMaterialList = [
          ...updatedCategoryWise[categoryIndex].company_material_master,
        ];
        const updatedMaterialDetails = [
          ...updatedMaterialList[subCategoryIndex].material_details,
        ];

        updatedMaterialDetails[detailIndex].unit_of_measurement = value;
        updatedMaterialDetails[detailIndex].unit_of_measurement_name =
          unitOfMeasurementt?.unit_of_measurement || value;

        updatedMaterialList[subCategoryIndex].material_details =
          updatedMaterialDetails;
        updatedCategoryWise[categoryIndex].company_material_master =
          updatedMaterialList;

        return {
          ...prevState,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [unitOfMeasurement],
  );

  const handleCheckBoxTypename = useCallback(
    (siteIndex, categoryIndex, subIndex, detailIndex, value) => {
      setState((prevState) => {
        const updatedCategoryWise = [...prevState.category_wise];
        const updatedMaterialDetails = [
          ...updatedCategoryWise[categoryIndex].company_material_master[
            subIndex
          ].material_details,
        ];

        updatedMaterialDetails[detailIndex] = {
          ...updatedMaterialDetails[detailIndex],
          is_checked: value,
        };

        updatedCategoryWise[categoryIndex].company_material_master[
          subIndex
        ].material_details = updatedMaterialDetails;
        return {
          ...prevState,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [],
  );

  const handleSelectAllChange = useCallback(
    (e, categoryIndex, subCategoryIndex) => {
      const value = e.target.checked;

      setSelectAllChecked((prev) => ({
        ...prev,
        [`${categoryIndex}_${subCategoryIndex}`]: value,
      }));

      setState((prev) => {
        const updatedCategoryWise = [...prev.category_wise];
        updatedCategoryWise[categoryIndex].company_material_master[
          subCategoryIndex
        ].material_details = updatedCategoryWise[
          categoryIndex
        ].company_material_master[subCategoryIndex].material_details.map(
          (detail) => ({ ...detail, is_checked: value }),
        );

        return {
          ...prev,
          category_wise: updatedCategoryWise,
        };
      });
    },
    [],
  );

  const handleTabChange = useCallback((key) => {
    setSelectedCategory(key);
  }, []);

  useEffect(() => {
    MaterialListCompanyGetById();
  }, [reload]);

  useEffect(() => {
    getUnitOfMeasurement();
  }, []);

  const getUnitOfMeasurement = async () => {
    try {
      const response = await getRequest("get-category-by-uom/?category_tree=1");
      const filteredData = response.data.results.data.filter(
        (item) => item.unit_of_measurement !== "Cubic Meter",
      );
      setUnitOfMeasurement(filteredData);
    } catch (error) {
      console.error("Error fetching unit of measurements:", error);
    }
  };

  const formatToIndianNumberSystem = (value) => {
    if (!value) return "";
    const number = Number(value.toString().replace(/,/g, ""));
    return isNaN(number) ? value : number.toLocaleString("en-IN");
  };

  const activeTabContent = useMemo(() => {
    if (!state?.category_wise?.length) return null;

    const currentCategory = state.category_wise.find(
      (cat) => cat.category_tree_name === selectedCategory,
    );
    if (!currentCategory) return null;

    return (
      <Form
        autoComplete="off"
        ref={formRef}
        onFinish={handleSubmit}
        className="tabscrolldivwrap"
      >
        <div className="tabscrolldiv">
          {currentCategory.company_material_master.map((item, subIndex) => {
            return (
              <MaterialMasterCategoryWise
                key={`${selectedCategory}-${subIndex}`}
                item={item}
                subIndex={subIndex}
                handleSelectAllChange={handleSelectAllChange}
                handleCheckBoxTypename={handleCheckBoxTypename}
                handleAddressChange={handleAddressChange}
                handleEstimatedPriceChange={handleEstimatedPriceChange}
                handleERPCodeChange={handleERPCodeChange}
                estimatedPrices={estimatedPrices}
                erpCodes={erpCodes}
                // unitOfMeasurement={unitOfMeasurement}
                colors={["#094E8F"]}
                selectAllChecked={selectAllChecked}
                categoryIndex={state.category_wise.findIndex(
                  (cat) => cat.category_tree_name === selectedCategory,
                )}
                cat={currentCategory}
                setPriceMode={setPriceMode}
                priceMode={priceMode}
                handleDiameterPriceChange={handleDiameterPriceChange}
                diameterPrices={item.material_details.reduce(
                  (acc, detail, detailIndex) => {
                    acc[detailIndex] = detail.diameter_prices || {};
                    return acc;
                  },
                  {},
                )}
                setState={setState}
                selectedCategory={selectedCategory}
              />
            );
          })}
        </div>
        <div className="stepsFooter">
          <Button
            type="primary"
            htmlType="submit"
            className="primary"
            loading={btnLoading}
          >
            Save
          </Button>
        </div>
      </Form>
    );
  }, [
    state?.category_wise,
    selectedCategory,
    handleSubmit,
    handleSelectAllChange,
    handleCheckBoxTypename,
    handleAddressChange,
    handleEstimatedPriceChange,
    handleERPCodeChange,
    estimatedPrices,
    erpCodes,
    unitOfMeasurement,
    priceMode,
    handleDiameterPriceChange,
  ]);

  const tabItems = useMemo(() => {
    if (!state?.category_wise?.length) return [];

    return state.category_wise.map((cat) => ({
      key: cat.category_tree_name,
      label: (
        <div className="tab-label">
          <div className="tab-text">{cat.category_tree_name}</div>
        </div>
      ),
      children:
        selectedCategory === cat.category_tree_name ? activeTabContent : null,
    }));
  }, [state?.category_wise, selectedCategory, activeTabContent]);
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Material Master</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>

              <Breadcrumb.Item>Material Master</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div className="pgnmbtns">
            <Button
              className="SaveGradiantBtn"
              onClick={handleSubmit}
              loading={btnLoading}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      <section className="grid-sec FormSection Container">
        {authUserData().company_role === "Company Admin" &&
          authUserData().employee === null && <SubscriptionNotification />}
        <div className="FormBody" style={{ padding: "0" }}>
          <div className="stepscontentwrap">
            <Spin spinning={loading}>
              {state?.category_wise?.length > 0 && (
                <Tabs
                  activeKey={
                    selectedCategory ||
                    state.category_wise[0]?.category_tree_name
                  }
                  onChange={handleTabChange}
                  tabBarGutter={24}
                  className="inr-tabs"
                  destroyInactiveTabPane={false}
                  items={tabItems}
                />
              )}
            </Spin>
          </div>
        </div>
      </section>
    </>
  );
};

export default MaterialListCompany;
